import { DecimalPipe } from "@angular/common";
import { Component, OnInit, Input, Output, EventEmitter, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { AdjustmentArchive } from "../vibe-history/vibe-history.component";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities, RECENT_OPERATIONS_LIMIT } from "src/app/dvs-utilities";
import { DatetimePipe, RuntimePipe } from "src/app/pipes";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-rotor-track",
	templateUrl: "./rotor-track.component.html",
	styleUrls: ["./rotor-track.component.scss"],
	imports: [FormsModule, DvsChartComponent, DecimalPipe, DatetimePipe, RuntimePipe]
})
export class RotorTrackComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);
	readonly RECENT_OPERATIONS_LIMIT = RECENT_OPERATIONS_LIMIT;

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;
	@Output()
	recordAdjustment = new EventEmitter<AdjustmentArchive>();

	public GetOpClass = DvsUtilities.GetOpClass;
	public operations: APIResults.Operation[];
	public operation?: APIResults.Operation;
	public regimeCounts: APIResults.RegimeCount[];
	public selectedRegimes: number[];
	public adjustments?: number[][];
	public alternates?: number[][][];
	public trackOptions: Highcharts.Options;
	public adjustmentSelect: boolean[];
	public regimeSplit: any;
	public errormessage?: string;
	public onePerRevOnly = true;

	public adjDetail: boolean[];

	ngOnInit() {
		this.adjustmentSelect = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
		this.adjDetail = Array(this.rotor.balancedata.numadjustmenttypes).fill(false);
		this.refreshOperations();
	}

	rotorChange() {
		this.ngOnInit();
	}

	async refreshOperations() {
		this.operations = undefined;
		this.operation = undefined;
		this.regimeCounts = undefined;
		const operations = await this.api.GetRTBOperations(this.asset.asset_id, this.rotor.rotorid, true, RECENT_OPERATIONS_LIMIT);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.operation = selectedOp;
			await this.refreshRegimeCounts();
		}
	}

	/** Must fire when an operation is selected to get the counts */
	async refreshRegimeCounts() {
		if (this.operation && this.rotor) {
			this.regimeCounts = undefined;
			const counts = await this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.operation.operation_id);
			// Populate regime names
			if (counts && counts.track) {
				let evalempty = true;
				for (const regime of counts.track) {
					regime.name = this.regimeConfigs[regime.regime_id].name;
					regime.evaluate = this.regimeConfigs[regime.regime_id].default;
					if (regime.evaluate) {
						evalempty = false;
					}
				}
				if (evalempty && counts.track.length) {
					counts.track[counts.track.length - 1].evaluate = true;
				}
				this.regimeCounts = counts.track;
			}
		}
		this.regimesChange();
	}

	regimesChange() {
		this.selectedRegimes = this.regimeCounts.filter(item => item.evaluate).map(item => item.regime_id);
		this.updatePlot();
	}

	async updatePlot() {
		this.adjustments = undefined;
		this.alternates = undefined;
		this.errormessage = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			this.trackOptions = undefined;
			try {
				// TODO: shaftorder is forced 1
				const results = await this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, [this.operation.operation_id], this.selectedRegimes, 1, this.adjustmentSelect, true, this.onePerRevOnly);
				this.fillPlot(results);
				this.adjustments = results.adjustments.map(item => item.map(cell => Number(cell.toFixed(1))));
				this.alternates = results.alternates;
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load RTB data";
			}
		}
	}
	SetAdjustment(adjIdx: number, altIdx: number) {
		const tmpAdj = this.adjustments[adjIdx];
		this.adjustments[adjIdx] = this.alternates[adjIdx][altIdx];
		this.alternates[adjIdx][altIdx] = tmpAdj;
		this.adjDetail[adjIdx] = false;
	}

	async ManualAdjustment() {
		this.alternates = undefined;

		this.errormessage = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			this.trackOptions = undefined;
			try {
				// TODO: shaftorder is forced 1
				const results = await this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, [this.operation.operation_id], this.selectedRegimes, 1, this.adjustmentSelect, true, this.onePerRevOnly, this.adjustments);
				this.fillPlot(results);
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load RTB data";
			}
		}
	}

	private fillPlot(rtbResults: APIResults.RTBResults) {
		this.regimeSplit = rtbResults.track;
		const series: Highcharts.SeriesScatterOptions[] = [];
		for (const regimeKey of Object.keys(rtbResults.track)) {
			const regime = this.regimeCounts.find(item => item.regime_id === Number(regimeKey));
			const regimedata = rtbResults.track[regimeKey];
			const points: any[] = [];
			points.push(...regimedata.predtrack.map((value, index) => {
				return {
					x: index,
					y: value, name: "Predicted Track",
					marker: { symbol: "diamond", radius: 7, lineColor: "white", lineWidth: 2 },
				};
			}));
			points.push(...regimedata.track.map((value, index) => {
				return {
					x: index,
					leadlag: regimedata.leadlag[index],
					y: value, name: "Current Track",
					marker: { symbol: "square", radius: 5, lineColor: "white", lineWidth: 2 },
				};
			}));
			series.push({
				name: regime.name,
				type: "scatter",
				data: points,
				marker: { symbol: "circle", radius: 4 },
			});
		}
		this.trackOptions = this.charting.RTBTrackDefaults(series, this.ReorderBlades(this.rotor.blade).map(item => item.name));
	}

	public ReorderBlades(filtered: any[]) {
		if (this.rotor.balancedata.clockwise && filtered.length > 1) {
			// Reorder blades if clockwise, see email "sorry, learned something new" 9/24/2025
			const reordered = filtered.slice().reverse();
			reordered.unshift(reordered.pop());
			return reordered;
		}
		return filtered;
	}

	RecordAdjustment() {
		const reordered = this.adjustments.map(item => this.ReorderBlades(item));
		const archive: AdjustmentArchive = {
			operation_id: this.operation.operation_id,
			operation_start: this.operation.start_time,
			adjustments: reordered,
			isClockwise: this.rotor.balancedata.clockwise
		};
		this.recordAdjustment.emit(archive);
	}
}
